import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import {
  GraduationCap, ArrowRight, Layers, PenTool, BookOpen, BookOpenText, Globe,
  Briefcase, Mail, Clock, BookMarked, DollarSign, Crown, Lock,
} from "lucide-react";
import { TRAINING_COURSES } from "../data/training_courses";

/* /dashboard/academy — THE ACADEMY.
   Single training hub for the entire platform. Two categories:
     1. HOW TO USE FAITHARAMA — platform-tool tutorials
     2. AFFILIATE COURSES     — money-making playbooks
   No standalone Pro Vault, no separate apologetics page. One name,
   one place, one categorization.                                    */

const NAVY = "#0F172A";
const GOLD = "#2E9F3A";
const ORANGE = "#F39A1F";
const CREAM = "#FFFFFF";

const ICONS = { Layers, PenTool, BookOpen, BookOpenText, Globe, Briefcase, Mail };

/* AFFILIATE COURSES — moved out of the deleted Pro Vault.  Mike records
   each one as he records it.  "Affiliate Marketing 101" is live; the rest
   are in production. */
const AFFILIATE_COURSES = [
  { img: "/logos/bibleproject.png",         title: "★ BibleProject — Free Bible Courses", desc: "200+ animated Bible videos. 100% free, forever. Faitharama brings you the best Bible studies in the business.", to: "/bibleproject", live: true },
  { img: "/courses/pro-affiliate-101.png",    title: "Affiliate Marketing 101",          desc: "The beginner playbook. 10 chapters · 90-day action plan.",            to: "/business/affiliate-marketing-101", live: true },
  { img: "/courses/pro-funnel-blueprint.png", title: "$10K Funnel Blueprint",             desc: "The exact funnel template Mike uses to convert cold traffic.",        live: false },
  { img: "/courses/pro-sales-page-vault.png", title: "Sales Page Vault — 12 Templates",   desc: "12 copy-and-paste sales pages you can rebrand and ship today.",       live: false },
  { img: "/courses/pro-ad-mastery.png",       title: "Ad Mastery",                        desc: "Facebook + YouTube + Pinterest paid traffic walkthroughs.",           live: false },
  { img: "/courses/pro-webinar-playbook.png", title: "Webinar Playbook",                  desc: "Convert 30% of attendees with this live + replay framework.",         live: false },
];

export default function TrainingHubPage({ embedded = false }) {
  useEffect(() => {
    document.title = "The Academy — Faitharama";
  }, []);

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="training-hub-page">
      {!embedded && <SiteNav />}

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden border-b-2"
               style={{ background: "linear-gradient(180deg, #B7D6EE 0%, #7FB3D9 60%, #3F7AAB 100%)", borderColor: GOLD }}>
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{
               background:
                 "radial-gradient(ellipse 60% 40% at 20% 30%, #fff 0%, transparent 60%), " +
                 "radial-gradient(ellipse 50% 30% at 80% 50%, #fff 0%, transparent 60%)",
             }} />
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
             style={{ background: `linear-gradient(180deg, transparent 0%, ${GOLD}55 100%)` }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20 text-center">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-3" style={{ color: NAVY }}>
            ★ THE ACADEMY · ALL TRAINING IN ONE PLACE
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl uppercase leading-[1.05] mx-auto max-w-4xl"
              style={{ color: NAVY, textWrap: "balance" }}>
            Learn The Tools. <span style={{ color: GOLD }}>Earn The Income.</span>
          </h1>
          <p className="mt-5 text-base md:text-xl max-w-3xl mx-auto leading-relaxed font-medium" style={{ color: NAVY }}>
            Two categories. Pick what you need. Every course is plain English, senior-friendly, and free for every member.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-2.5 text-[11px] uppercase tracking-[0.18em] font-bold">
            <span className="px-3 py-2 rounded-md" style={{ background: "#FFFFFF", color: NAVY, border: `1.5px solid ${GOLD}` }}>
              <Clock className="w-3.5 h-3.5 inline mr-1.5" /> 20–35 min each
            </span>
            <span className="px-3 py-2 rounded-md" style={{ background: "#FFFFFF", color: NAVY, border: `1.5px solid ${GOLD}` }}>
              <BookMarked className="w-3.5 h-3.5 inline mr-1.5" /> Read at your pace
            </span>
            <span className="px-3 py-2 rounded-md" style={{ background: GOLD, color: NAVY, border: `1.5px solid ${NAVY}` }}>
              <DollarSign className="w-3.5 h-3.5 inline mr-1.5" /> Included in every plan
            </span>
          </div>
        </div>
      </section>

      {/* ═══ CATEGORY 1 · HOW TO USE FAITHARAMA ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="academy-section-platform">
        <CategoryHeader
          eyebrow="CATEGORY 1"
          title="How To Use Faitharama"
          desc="Tool-by-tool tutorials. Funnels, emails, mirror site, AI writer — everything you need to run the platform."
          color={GOLD}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="training-course-grid">
          {TRAINING_COURSES.map((c) => {
            const Icon = ICONS[c.icon] || GraduationCap;
            return (
              <Link key={c.slug} to={`/academy/${c.slug}`}
                    data-testid={`training-card-${c.slug}`}
                    className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white block p-6"
                    style={{ borderColor: `${c.accent}66` }}>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: c.accent }}>
                  <span className="px-2 py-0.5 rounded-full text-white" style={{ background: c.accent }}>★ {c.number}</span>
                  Platform Course
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="w-9 h-9" style={{ color: c.accent }} />
                  <div className="font-serif-display text-2xl leading-tight" style={{ color: NAVY, fontWeight: 700 }}>
                    {c.title}
                  </div>
                </div>
                <p className="text-sm text-foreground/85 leading-relaxed mb-4">{c.blurb}</p>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-foreground/55">
                    <Clock className="w-3.5 h-3.5 inline mr-1" /> {c.minutes} min · {c.chapters.length} lessons
                  </div>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold" style={{ color: c.accent }}>
                    Start <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ═══ CATEGORY 2 · AFFILIATE COURSES ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 border-t" style={{ borderColor: "#0F172A22" }} data-testid="academy-section-affiliate">
        <CategoryHeader
          eyebrow="CATEGORY 2"
          title="Affiliate Courses"
          desc="The money-making playbooks. How to turn shares, links, and follow-ups into commission."
          color={ORANGE}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="affiliate-course-grid">
          {AFFILIATE_COURSES.map((c) => {
            const Card = (
              <div className="group rounded-2xl overflow-hidden border-2 shadow-md hover:shadow-2xl hover:-translate-y-1 transition bg-white h-full flex flex-col"
                   style={{ borderColor: c.live ? `${ORANGE}88` : `${NAVY}22` }}>
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
                  <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover"
                       onError={(e) => { e.target.style.display = "none"; }}/>
                  {!c.live && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase font-bold text-white"
                            style={{ background: NAVY }}>
                        <Lock className="w-3 h-3"/> Recording Soon
                      </span>
                    </div>
                  )}
                  {c.live && (
                    <span className="absolute top-3 left-3 text-[10px] tracking-[0.22em] uppercase font-bold px-2 py-1 rounded-full text-white"
                          style={{ background: ORANGE }}>★ LIVE NOW</span>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: ORANGE }}>
                    <Crown className="w-3.5 h-3.5"/> Affiliate Course
                  </div>
                  <div className="font-serif-display text-xl leading-tight mb-2" style={{ color: NAVY, fontWeight: 700 }}>
                    {c.title}
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed flex-1">{c.desc}</p>
                  {c.live && (
                    <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold" style={{ color: ORANGE }}>
                      Open Course <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            );
            return c.live
              ? <Link key={c.title} to={c.to} data-testid={`affiliate-course-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>{Card}</Link>
              : <div key={c.title} data-testid={`affiliate-course-soon-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>{Card}</div>;
          })}
        </div>
      </section>

      {!embedded && <SiteFooter />}
    </div>
  );
}

function CategoryHeader({ eyebrow, title, desc, color }) {
  return (
    <div className="max-w-3xl">
      <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color }}>★ {eyebrow}</div>
      <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-2" style={{ color: NAVY, textWrap: "balance" }}>
        {title}
      </h2>
      <p className="mt-3 text-base md:text-lg text-foreground/75 leading-relaxed">{desc}</p>
    </div>
  );
}
